import { useState, useEffect } from 'react';
import apiClient from '../../../lib/api';
import { resolveImageUrl } from '../../../lib/utils';

// ✅ Permission helper
const hasPermFor = (id: string) => {
  try {
    const raw =
      localStorage.getItem('adminPermissions') ||
      localStorage.getItem('permissions') ||
      '[]';
    const perms = JSON.parse(raw);
    return perms.some((p: any) => p.id === id && p.enabled);
  } catch {
    return true; // default allow
  }
};

interface Slide {
  id: string;
  slide_number: number;
  title: string;
  subtitle: string;
  description: string;
  image_url: string;
  cta_text: string;
  cta_link: string;
  is_active: number;
  created_at?: string;
  updated_at?: string;
}

const getImageUrl = (url: string | null | undefined) => {
  if (!url) return '';
  if (url.startsWith('data:') || url.startsWith('blob:')) return url;
  return resolveImageUrl(url);
};

const HeroSliderManager = () => {
  const [slides, setSlides] = useState<Slide[]>([]);
  const [loading, setLoading] = useState(true);

  const [editingSlide, setEditingSlide] = useState<Slide | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  useEffect(() => {
    loadSlides();
  }, []);

  const loadSlides = async () => {
    try {
      setLoading(true);
      const slidesData = await apiClient.getHeroSlides();
      setSlides(slidesData.sort((a: Slide, b: Slide) => a.slide_number - b.slide_number));
    } catch (error) {
      console.error('Failed to load slides:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditSlide = (slide: Slide) => {
    setEditingSlide({ ...slide });
    setSelectedFile(null);
    setShowModal(true);
  };

  const handleSaveSlide = async () => {
    if (!editingSlide) return;

    try {
      const isUpdate = slides.find((s) => s.id === editingSlide.id);

      // Create FormData for file upload
      const formData = new FormData();

      // Add all slide fields
      Object.keys(editingSlide).forEach(key => {
        const val = editingSlide[key as keyof Slide];
        if (val !== undefined) {
          // Skip sending large base64 strings to save bandwidth/prevent timeouts
          if (key === 'image_url' && selectedFile) return;
          if (key === 'image_url' && typeof val === 'string' && val.startsWith('data:')) return;

          formData.append(key, String(val));
        }
      });

      // Add file if selected
      if (selectedFile) {
        formData.append('image', selectedFile);
        console.log('Uploading file:', selectedFile.name);
      }

      if (isUpdate) {
        await apiClient.updateHeroSlide(editingSlide.id, formData);
      } else {
        await apiClient.createHeroSlide(formData);
      }

      await loadSlides();
      setShowModal(false);
      setEditingSlide(null);
      setSelectedFile(null);
    } catch (error) {
      console.error('Failed to save slide:', error);
    }
  };

  const handleAddSlide = () => {
    const newSlide: Slide = {
      id: '',
      slide_number: slides.length + 1,
      title: 'New Slide',
      subtitle: 'Slide subtitle',
      description: 'Slide description',
      image_url: '',
      cta_text: 'Learn More',
      cta_link: '/',
      is_active: 1
    };
    setEditingSlide(newSlide);
    setSelectedFile(null);
    setShowModal(true);
  };

  const handleDeleteSlide = async (id: string) => {
    console.log('[HeroSliderManager] handleDeleteSlide called with id:', id);
    if (slides.length <= 1) {
      console.warn('[HeroSliderManager] Cannot delete last remaining slide');
      alert('Cannot delete the last remaining slide. Add a new one first.');
      return;
    }

    const ok = window.confirm('Are you sure you want to delete this slide?');
    console.log('[HeroSliderManager] confirm returned:', ok);
    if (!ok) return;

    try {
      console.log('[HeroSliderManager] Proceeding with API delete for slide:', id);
      await apiClient.deleteHeroSlide(id);
      console.log('[HeroSliderManager] Delete successful, reloading data');
      await loadSlides();
    } catch (error) {
      console.error('[HeroSliderManager] Failed to delete slide:', error);
      alert('Failed to delete slide. Please try again.');
    }
  };

  const toggleSlideStatus = async (id: string) => {
    try {
      const slide = slides.find(s => s.id === id);
      if (slide) {
        await apiClient.updateHeroSlide(id, { ...slide, is_active: slide.is_active ? 0 : 1 });
        await loadSlides();
      }
    } catch (error) {
      console.error('Failed to toggle slide status:', error);
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        if (editingSlide && e.target?.result) {
          setEditingSlide({
            ...editingSlide,
            image_url: e.target.result as string
          });
        }
      };
      reader.readAsDataURL(file);
    }
  };

  // 🚫 Hide if no permission - temporarily disabled for testing
  // if (!hasPermFor('home-hero')) {
  //   return null;
  // }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-400"></div>
        <span className="ml-2">Loading slides...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-bold text-gray-900">
          Hero Slider Management
        </h3>
        <button
          onClick={handleAddSlide}
          className="bg-primary-400 hover:bg-primary-500 text-black px-4 py-2 rounded-lg font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer flex items-center"
        >
          <i className="ri-add-line mr-2"></i>
          Add Slide
        </button>
      </div>

      <div className="grid grid-cols-1 gap-4">
        {slides.map((slide) => (
          <div
            key={slide.id}
            className="bg-gray-50 rounded-lg p-4 border border-gray-200"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-3">
                <h4 className="font-semibold text-gray-900">{slide.title}</h4>
                <span
                  className={`px-2 py-1 text-xs rounded-full ${slide.is_active
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                    }`}
                >
                  {slide.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => toggleSlideStatus(slide.id)}
                  className="text-gray-600 hover:text-gray-800 cursor-pointer"
                >
                  <i
                    className={slide.is_active ? 'ri-eye-line' : 'ri-eye-off-line'}
                  ></i>
                </button>
                <button
                  onClick={() => handleEditSlide(slide)}
                  className="text-blue-600 hover:text-blue-800 cursor-pointer"
                >
                  <i className="ri-edit-line"></i>
                </button>
                {slides.length > 1 && (
                  <button
                    onClick={() => handleDeleteSlide(slide.id)}
                    className="text-red-600 hover:text-red-800 cursor-pointer"
                  >
                    <i className="ri-delete-bin-line"></i>
                  </button>
                )}
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Image:</span>
                <div className="mt-1">
                  {slide.image_url ? (
                    <img
                      src={getImageUrl(slide.image_url)}
                      alt={slide.title}
                      className="w-full h-20 object-cover rounded"
                    />
                  ) : (
                    <div className="w-full h-20 bg-gray-200 rounded flex items-center justify-center">
                      No Image
                    </div>
                  )}
                </div>
              </div>
              <div>
                <span className="text-gray-600">Description:</span>
                <p className="mt-1 text-gray-900">{slide.description}</p>
              </div>
              <div>
                <span className="text-gray-600">Button:</span>
                <p className="mt-1 text-gray-900">{slide.cta_text}</p>
              </div>
              <div>
                <span className="text-gray-600">Link:</span>
                <p className="mt-1 text-gray-900">{slide.cta_link}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Edit Modal */}
      {showModal && editingSlide && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900">
                {slides.find((s) => s.id === editingSlide.id)
                  ? 'Edit Slide'
                  : 'Add New Slide'}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-500 hover:text-gray-700 cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    Title
                  </label>
                  <input
                    type="text"
                    value={editingSlide.title}
                    onChange={(e) =>
                      setEditingSlide({ ...editingSlide, title: e.target.value })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    Button Text
                  </label>
                  <input
                    type="text"
                    value={editingSlide.cta_text}
                    onChange={(e) =>
                      setEditingSlide({
                        ...editingSlide,
                        cta_text: e.target.value
                      })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-gray-700 font-semibold mb-2">
                  Subtitle
                </label>
                <input
                  type="text"
                  value={editingSlide.subtitle}
                  onChange={(e) =>
                    setEditingSlide({
                      ...editingSlide,
                      subtitle: e.target.value
                    })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-semibold mb-2">
                  Description
                </label>
                <textarea
                  value={editingSlide.description}
                  onChange={(e) =>
                    setEditingSlide({
                      ...editingSlide,
                      description: e.target.value
                    })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                  rows={3}
                />
              </div>

              <div>
                <label className="block text-gray-700 font-semibold mb-2">
                  Upload Image
                </label>
                <div className="space-y-2">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileUpload}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                  />
                  <p className="text-sm text-gray-600">
                    or enter image URL below
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    Image URL
                  </label>
                  <input
                    type="url"
                    value={editingSlide.image_url}
                    onChange={(e) =>
                      setEditingSlide({
                        ...editingSlide,
                        image_url: e.target.value
                      })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                    placeholder="https://example.com/image.jpg"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    Button Link
                  </label>
                  <input
                    type="text"
                    value={editingSlide.cta_link}
                    onChange={(e) =>
                      setEditingSlide({
                        ...editingSlide,
                        cta_link: e.target.value
                      })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                    placeholder="/services"
                  />
                </div>
              </div>

              {editingSlide.image_url && (
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    Preview
                  </label>
                  <img
                    src={getImageUrl(editingSlide.image_url)}
                    alt="Preview"
                    className="w-full h-40 object-cover rounded-lg"
                  />
                </div>
              )}

              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="slideActive"
                  checked={!!editingSlide.is_active}
                  onChange={(e) =>
                    setEditingSlide({
                      ...editingSlide,
                      is_active: e.target.checked ? 1 : 0
                    })
                  }
                  className="w-4 h-4 text-primary-400 border-gray-300 rounded focus:ring-primary-400"
                />
                <label
                  htmlFor="slideActive"
                  className="text-gray-700 font-semibold"
                >
                  Active
                </label>
              </div>
            </div>

            <div className="flex justify-end space-x-4 mt-6">
              <button
                onClick={() => setShowModal(false)}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveSlide}
                className="px-6 py-2 bg-primary-400 hover:bg-primary-500 text-black rounded-lg font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                Save Slide
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HeroSliderManager;
